import styles from 'components/FIXMessage/fix-message.module.scss';
import execTypes from 'constants/execTypes';
import { toClassName } from 'helpers/toClassName';
import { FIXTaggedField, isNumberField, isTextField } from 'interfaces/FIXTaggedField';
import moment, { Moment } from 'moment';
import React from 'react';

type Props = FIXTaggedField;

interface ValueProps {
  readonly value: string | number;
}

const FIXFieldValue: React.FC<ValueProps> = (props: ValueProps): React.ReactElement => {
  const { value } = props;

  if (typeof value === 'string' || typeof value === 'number') {
    return <span>{value}</span>;
  } else {
    return <span className={styles.na}>N/A</span>;
  }
};

const FIXFieldDateTimeValue: React.FC<ValueProps> = (props: ValueProps): React.ReactElement => {
  const { value } = props;

  const converted = React.useMemo((): Moment | null => {
    if (typeof value === 'number') {
      return moment.unix(value);
    }
    const str = String(value).trim();
    if (!str) return null;
    // Parse as UTC: support 12-hour "02/12/2026 8:32:30 PM" or 24-hour with optional ms
    const formats = [
      'MM/DD/YYYY h:mm:ss.SSS A',
      'MM/DD/YYYY h:mm:ss A',
      'MM/DD/YYYY HH:mm:ss.SSS',
      'MM/DD/YYYY HH:mm:ss',
    ];
    const m = moment.utc(str, formats, true);
    if (!m.isValid()) return null;
    // String is in UTC; display in the user's local browser timezone
    return m.local();
  }, [value]);

  if (converted) {
    return (
      <span>
        {converted.format('L')} {converted.format('LTS')}
      </span>
    );
  }
  return <span className={styles.na}>N/A</span>;
};

export const FIXField: React.FC<Props> = (props: Props): React.ReactElement | null => {
  if (props.value === null) {
    return (
      <div className={styles.field} key={props.label}>
        <div className={styles.label}>{props.label}</div>
        <div className={toClassName(styles.value, styles[props.type])}>
          <span className={styles.na}>N/A</span>
        </div>
      </div>
    );
  } else if (isNumberField(props)) {
    return (
      <div className={styles.field} key={props.label}>
        <div className={styles.label}>{props.label}</div>
        <div className={toClassName(styles.value, styles[props.type])}>
          <FIXFieldValue value={props.value} />
        </div>
      </div>
    );
  } else if (!isTextField(props)) {
    return (
      <div className={styles.field} key={props.label}>
        <div className={styles.label}>{props.label}</div>
        <div className={toClassName(styles.value, styles[props.type])}>
          <span>?</span>
        </div>
      </div>
    );
  } else if (props.label === 'SendingTime' || props.label === 'TransactTime') {
    return (
      <div className={styles.field} key={props.label}>
        <div className={styles.label}>{props.label}</div>
        <div className={toClassName(styles.value, styles[props.type])}>
          <FIXFieldDateTimeValue value={props.value} />
        </div>
      </div>
    );
  } else {
    return (
      <div className={styles.field} key={props.label}>
        <div className={styles.label}>{props.label}</div>
        <div className={toClassName(styles.value, styles[props.type])}>
          <FIXFieldValue
            value={
              props.label === 'ExecType' && Number(props.value)
                ? execTypes[props.value]
                : props.value
            }
          />
        </div>
      </div>
    );
  }
};
